import React, { useState } from 'react';
import { ChevronLeft, MapPin, Clock, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import useHeaderOffset from '@/hooks/useHeaderOffset';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
} from '@/components/ui/carousel';
import ImageViewer from '@/components/ImageViewer';
import type { Tour } from './ToursCardList';

interface TourInfoSidebarProps {
  tour: Tour;
  onBack: () => void;
}

export const TourInfoSidebar: React.FC<TourInfoSidebarProps> = ({ tour, onBack }) => {
  const [isImageViewerOpen, setIsImageViewerOpen] = useState(false);
  const [viewerStartIndex, setViewerStartIndex] = useState(0);
  const { isDesktop } = useHeaderOffset();

  // Handle ESC key to prevent closing sidebar when ImageViewer is open
  React.useEffect(() => {
    if (!isImageViewerOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        setIsImageViewerOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown, true); // Use capture phase
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [isImageViewerOpen]);

  const formatTimeToAMPM = (time: string): string => {
    if (!time) return '';
    const [hours, minutes] = time.split(':').slice(0, 2);
    const h = parseInt(hours, 10);
    const m = minutes;
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${m} ${ampm}`;
  };

  const cleanHTMLTags = (text: string): string => {
    if (!text) return '';
    return text
      .replace(/<[^>]*>/g, '') // Remover todas las etiquetas HTML
      .replace(/&nbsp;/g, ' ')  // Reemplazar espacios HTML
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#039;/g, "'")
      .trim();
  };

  // Build display images array (main image first, then others)
  const displayImageUrls: string[] = [];
  if (tour.main_image_url) {
    displayImageUrls.push(tour.main_image_url);
    if (tour.image_urls) {
      tour.image_urls.forEach(url => {
        if (url !== tour.main_image_url) {
          displayImageUrls.push(url);
        }
      });
    }
  } else if (tour.image_urls && tour.image_urls.length > 0) {
    displayImageUrls.push(...tour.image_urls);
  } else if (tour.details?.image_url) {
    displayImageUrls.push(tour.details.image_url);
  }

  return (
    <div className="flex flex-col h-full overflow-hidden bg-background">
      {/* Safe Area Spacer for Mobile */}
      {!isDesktop && <div className="h-[env(safe-area-inset-top,0px)] bg-primary w-full shrink-0" />}

      {/* Header con back button y título */}
      <div className="flex items-center p-4 border-b shrink-0 bg-primary text-white gap-2 z-10">
        <button
          onClick={onBack}
          className="p-1 hover:bg-white/10 text-white rounded-lg transition-colors flex-shrink-0"
          aria-label="Volver"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <h2 className="font-semibold text-lg text-white truncate flex-1">{tour.title}</h2>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden scrolling-touch custom-scrollbar overscroll-contain pb-20">
        <div className="p-4 space-y-4">
          {/* Carrusel de fotos */}
          {displayImageUrls.length > 0 && (
            <div className="w-full">
              <Carousel className="w-full">
                <CarouselContent>
                  {displayImageUrls.map((imageUrl, index) => (
                    <CarouselItem key={index}>
                      <div 
                        className="relative w-full aspect-video bg-muted rounded-lg overflow-hidden cursor-pointer"
                        onClick={() => {
                          setViewerStartIndex(index);
                          setIsImageViewerOpen(true);
                        }}
                      >
                        <img
                          src={imageUrl}
                          alt={`${tour.title} - Imagen ${index + 1}`}
                          className="absolute inset-0 w-full h-full object-cover hover:opacity-90 transition-opacity"
                          loading={index === 0 ? 'eager' : 'lazy'}
                        />
                      </div>
                    </CarouselItem>
                  ))}
                </CarouselContent>
                {displayImageUrls.length > 1 && (
                  <>
                    <CarouselPrevious />
                    <CarouselNext />
                  </>
                )}
              </Carousel>
            </div>
          )}

          {/* Título */}
          <div>
            <h3 className="text-lg font-bold">{tour.title}</h3>
            {tour.store_name && (
              <p className="text-xs text-muted-foreground mt-1">Operador: {tour.store_name}</p>
            )}
          </div>

          {/* Descripción */}
          {tour.description && (
            <div>
              <h4 className="text-sm font-semibold mb-2">Descripción</h4>
              <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                {cleanHTMLTags(tour.description)}
              </p>
            </div>
          )}

          {/* Detalles principales */}
          <div className="space-y-3 pt-2 border-t">
            {/* Horario de salida */}
            {tour.departure_time && (
              <div className="flex items-center gap-3">
                <Clock className="w-4 h-4 text-muted-foreground shrink-0" />
                <div>
                  <p className="text-xs text-muted-foreground">Horario de Salida</p>
                  <p className="text-sm font-semibold">{formatTimeToAMPM(tour.departure_time)}</p>
                </div>
              </div>
            )}

            {/* Ubicación de origen */}
            {tour.details?.origin?.name && (
              <div className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs text-muted-foreground">Punto de Salida</p>
                  <p className="text-sm font-semibold">{tour.details.origin.name}</p>
                </div>
              </div>
            )}

            {/* Ubicación de destino */}
            {tour.details?.destination?.name && (
              <div className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs text-muted-foreground">Destino</p>
                  <p className="text-sm font-semibold">{tour.details.destination.name}</p>
                </div>
              </div>
            )}

            {/* Duración */}
            {tour.details?.duration_days && (
              <div className="flex items-center gap-3">
                <Clock className="w-4 h-4 text-muted-foreground shrink-0" />
                <div>
                  <p className="text-xs text-muted-foreground">Duración</p>
                  <p className="text-sm font-semibold">
                    {tour.details.duration_days} {tour.details.duration_days === 1 ? 'día' : 'días'}
                  </p>
                </div>
              </div>
            )}

            {/* Capacidad */}
            {tour.details?.capacity && (
              <div className="flex items-center gap-3">
                <Users className="w-4 h-4 text-muted-foreground shrink-0" />
                <div>
                  <p className="text-xs text-muted-foreground">Capacidad</p>
                  <p className="text-sm font-semibold">Hasta {tour.details.capacity} personas</p>
                </div>
              </div>
            )}

            {/* Precio */}
            <div className="flex items-center justify-between pt-3 border-t">
              <p className="text-sm font-medium">Precio por persona</p>
              <p className="text-lg font-bold text-primary">ARS ${tour.fixed_sale_price}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Image Viewer Modal */}
      <ImageViewer
        imageUrls={displayImageUrls}
        startIndex={viewerStartIndex}
        isOpen={isImageViewerOpen}
        onClose={() => setIsImageViewerOpen(false)}
      />

      {/* Footer button */}
      <div className="border-t pt-4 px-4 pb-[calc(1rem+env(safe-area-inset-bottom,0px))] space-y-2 bg-muted/20 shrink-0">
        <Button
          onClick={onBack}
          variant="outline"
          size="lg"
          className="w-full active:scale-95"
        >
          Volver a Excursiones
        </Button>
      </div>
    </div>
  );
};
